import React, { useId, useState } from 'react';
import * as TabsPrimitive from '@radix-ui/react-tabs';
import { motion } from 'framer-motion';
import { cn } from '@lqa-frontend/ui-system';

export type IAnimatedTabOption<T extends string = string> = {
	value: T;
	label: React.ReactNode;
	isDisabled?: boolean;
};

type ControlledProps<T extends string> = {
	value: T;
	onValueChange: (value: T) => void;
	defaultValue?: never;
};

type UncontrolledProps<T extends string> = {
	defaultValue: T;
	value?: never;
	onValueChange?: never;
};

export type IAnimatedTabsProps<T extends string = string> = {
	tabs: IAnimatedTabOption<T>[];
	children?: React.ReactNode;
	listClassName?: string;
	className?: string;
	/** Override the layoutId used for the pill animation. Useful when multiple instances are on the same page. */
	layoutId?: string;
} & (ControlledProps<T> | UncontrolledProps<T>);

export function AnimatedTabs<T extends string = string>({
	tabs,
	value: controlledValue,
	onValueChange,
	defaultValue,
	children,
	listClassName,
	className,
	layoutId,
}: IAnimatedTabsProps<T>) {
	const generatedId = useId();
	const pillId = layoutId ?? `animated-tabs-pill-${generatedId}`;

	const [internalValue, setInternalValue] = useState<T>(
		defaultValue ?? tabs[0]?.value ?? ('' as T)
	);

	const isControlled = controlledValue !== undefined;
	const activeValue = isControlled ? controlledValue : internalValue;

	function handleValueChange(next: string) {
		const typed = next as T;
		if (!isControlled) setInternalValue(typed);
		onValueChange?.(typed);
	}

	return (
		<TabsPrimitive.Root
			value={activeValue}
			onValueChange={handleValueChange}
			className={cn('flex flex-col', className)}
		>
			<TabsPrimitive.List
				className={cn(
					'relative flex h-36 w-full items-center rounded-m bg-base-2 p-4',
					listClassName
				)}
			>
				{tabs.map((tab) => {
					const isActive = activeValue === tab.value;
					return (
						<TabsPrimitive.Trigger
							key={tab.value}
							value={tab.value}
							disabled={tab.isDisabled}
							className={cn(
								'relative z-10 flex flex-1 cursor-pointer items-center justify-center rounded-s px-8 py-6',
								'text-12 font-semibold leading-lh6 outline-none transition-colors',
								'focus-visible:ring-2 focus-visible:ring-interactive focus-visible:ring-offset-1',
								'disabled:pointer-events-none disabled:cursor-not-allowed disabled:opacity-50',
								'data-[state=active]:bg-transparent data-[state=active]:shadow-none',
								isActive
									? 'text-text-primary'
									: 'text-text-secondary hover:text-text-primary'
							)}
						>
							{isActive && (
								<motion.div
									layoutId={pillId}
									className="absolute inset-0 rounded-s bg-white shadow-[0px_1px_2px_rgba(0,0,0,0.05),0px_1px_3px_rgba(0,0,0,0.1)]"
									transition={{ type: 'spring', stiffness: 400, damping: 35 }}
								/>
							)}
							<span className="relative z-10">{tab.label}</span>
						</TabsPrimitive.Trigger>
					);
				})}
			</TabsPrimitive.List>

			{children}
		</TabsPrimitive.Root>
	);
}

export function AnimatedTabsContent({
	className,
	...props
}: React.ComponentProps<typeof TabsPrimitive.Content>) {
	return (
		<TabsPrimitive.Content
			className={cn('outline-none', className)}
			{...props}
		/>
	);
}
